import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Modal, Badge, Card } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { userAPI } from '../api/userAPI'
import { usePermissions } from '../contexts/PermissionContext'
import TwoFactorManagement from '../components/TwoFactorManagement'
import { twoFactorAPI } from '../api/twoFactorAPI'

function UserUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [user, setUser] = useState({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        is_active: true,
        is_permission_admin: false
    })
    const [currentUser, setCurrentUser] = useState(null)
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

    useEffect(() => {
        async function fetchData() {
            try {
                const [userData, currentUserData] = await Promise.all([
                    userAPI.getById(id),
                    userAPI.getCurrentUser()
                ]);
                setUser(userData)
                setCurrentUser(currentUserData)
                setTwoFactorEnabled(userData.two_factor_enabled || false)
            } catch (error) {
                console.error('Error fetching user:', error)
                setError('Failed to load user data')
            }
        }
        fetchData()
    }, [id])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await userAPI.update(id, user);
            navigate('/users')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async (e) => {
        e.preventDefault();
        
        const currentUsername = localStorage.getItem('username');
        
        if (user.is_active) {
            if (user.username === currentUsername) {
                setShowDeactivateModal(true);
                return;
            } else {
                if (!window.confirm('Are you sure you want to deactivate this user?')) {
                    return;
                }
            }
        }

        await deactivateUser();
    };

    const deactivateUser = async () => {
        try {
            const updatedUser = {
                ...user,
                is_active: !user.is_active
            };

            await userAPI.update(id, updatedUser);
            setUser(updatedUser);

            const currentUsername = localStorage.getItem('username');
            if (user.username === currentUsername && !updatedUser.is_active) {
                localStorage.clear();
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Error toggling user status:', error);
            setError(`Failed to update user: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleAdminDisable2FA = async () => {
        try {
            await twoFactorAPI.adminDisable(id);
            setTwoFactorEnabled(false);
            setShowDisable2FAModal(false);
            // Optionally show success message
            setError(null);
        } catch (error) {
            console.error('Error disabling 2FA:', error);
            setError(`Failed to disable 2FA: ${error.response?.data?.error || error.message}`);
            setShowDisable2FAModal(false);
        }
    };

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('users.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to update users.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <h1 style={{ opacity: user.is_active ? 1 : 0.6 }} className="mb-0">
                                Update User
                            </h1>
                            {user.is_superuser && (
                                <Badge bg="success" className="fs-6">
                                    Superuser
                                </Badge>
                            )}
                            {user.is_permission_admin && (
                                <Badge bg="danger" className="fs-6">
                                    Permission Admin
                                </Badge>
                            )}
                        </div>
                        <Button
                            variant="outline-secondary"
                            onClick={() => navigate('/users')}
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </div>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="username">
                            <Form.Label>Username*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={user.username}
                                onChange={(e) => setUser({ ...user, username: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="first_name">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter first name"
                                value={user.first_name}
                                onChange={(e) => setUser({ ...user, first_name: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="last_name">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter last name"
                                value={user.last_name}
                                onChange={(e) => setUser({ ...user, last_name: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email"
                                value={user.email}
                                onChange={(e) => setUser({ ...user, email: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="permissionAdmin">
                            <Form.Label>Permission Admin (can manage other users&apos; permissions)</Form.Label>
                            <Form.Check
                                type="switch"
                                id="makePermissionAdmin"
                                checked={user.is_permission_admin || false}
                                onChange={(e) => setUser({ ...user, is_permission_admin: e.target.checked })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                            />
                        </Form.Group>

                        {/* Two-Factor Authentication Section */}
                        <Card className="mb-4">
                            <Card.Header className="bg-light">
                                <h5 className="mb-0">Two-Factor Authentication</h5>
                            </Card.Header>
                            <Card.Body>
                                {user.username === localStorage.getItem('username') ? (
                                    /* Current user - show full management component */
                                    <TwoFactorManagement />
                                ) : (
                                    /* Other user - show status and admin controls */
                                    <div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span>Status:</span>
                                            <Badge bg={twoFactorEnabled ? "success" : "secondary"}>
                                                {twoFactorEnabled ? "Enabled" : "Disabled"}
                                            </Badge>
                                        </div>
                                        {currentUser &&
                                         (currentUser.is_superuser || currentUser.is_permission_admin) &&
                                         twoFactorEnabled &&
                                         user.is_active && (
                                            <div className="mt-3">
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={() => setShowDisable2FAModal(true)}
                                                >
                                                    <i className="fas fa-shield-alt me-2"></i>
                                                    Disable 2FA for {user.username}
                                                </Button>
                                                <div className="text-muted small mt-2">
                                                    As an admin, you can disable 2FA for this user. They will need to re-enable it themselves.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Row className="mt-3">
                            <Col>
                                {user.is_active ? (
                                    <>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="me-2"
                                        >
                                            Update
                                        </Button>
                                        <Button
                                            variant="warning"
                                            onClick={() => navigate(`/user/${user.id}/change-password`)}
                                            className="me-2"
                                        >
                                            Change Password
                                        </Button>
                                        {currentUser && (currentUser.is_superuser || currentUser.is_permission_admin) && (
                                            <Button
                                                variant="info"
                                                onClick={() => navigate(`/user/${user.id}/permissions`)}
                                                className="me-2"
                                            >
                                                Permissions
                                            </Button>
                                        )}
                                        <Button
                                            variant="danger"
                                            onClick={handleToggleActive}
                                        >
                                            Deactivate
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="success"
                                        onClick={handleToggleActive}
                                    >
                                        Activate
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        {!user.is_active && (
                            <Alert variant="warning" className="mt-3">
                                This user is currently inactive. Activate it to make changes.
                            </Alert>
                        )}
                    </Form>
                </Col>
            </Row>


            {/* Deactivation Confirmation Modal */}
            <Modal
                show={showDeactivateModal}
                onHide={() => setShowDeactivateModal(false)}
                centered
            >
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Warning: Account Deactivation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-warning">
                        <h5>You are about to deactivate your own account!</h5>
                        <p>This action will have the following consequences:</p>
                        <ul className="mb-0">
                            <li>You will be immediately logged out</li>
                            <li>Your account will be locked</li>
                            <li>You will not be able to log in again</li>
                        </ul>
                    </div>
                    <p className="mb-0 text-danger">
                        Are you sure you want to proceed with this action?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeactivateModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            setShowDeactivateModal(false);
                            deactivateUser();
                        }}
                    >
                        Yes, Deactivate My Account
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Admin Disable 2FA Confirmation Modal */}
            <Modal
                show={showDisable2FAModal}
                onHide={() => setShowDisable2FAModal(false)}
                centered
            >
                <Modal.Header closeButton className="bg-warning">
                    <Modal.Title>
                        <i className="fas fa-shield-alt me-2"></i>
                        Disable 2FA for {user.username}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to disable Two-Factor Authentication for user <strong>{user.username}</strong>?
                    </p>
                    <div className="alert alert-info">
                        <strong>Note:</strong>
                        <ul className="mb-0 mt-2">
                            <li>All TOTP devices for this user will be removed</li>
                            <li>The user will need to set up 2FA again if they want to re-enable it</li>
                            <li>This action will be logged for audit purposes</li>
                        </ul>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDisable2FAModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        onClick={handleAdminDisable2FA}
                    >
                        Yes, Disable 2FA
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

export default UserUpdateScreen 